import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { getCookie } from "@insite/mobius/utilities/cookies";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    timerDuration = "timerDuration",
    buttonLabel = "buttonLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.timerDuration]: string;
        [fields.buttonLabel]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        session: state.context.session,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    signOut,
};

export interface ShareListModalStyles {
    modal?: ModalPresentationProps;
    container?: InjectableCss;
    ctaButtonsWrapper?: InjectableCss;
    logoutButton?: ButtonPresentationProps;
    contentWrapper?: InjectableCss;
    warning?: TypographyProps;
}

export const shareListModalStyles: ShareListModalStyles = {
    modal: {
        size: 944,
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
                border-bottom: none;
                height: 50px;
                [class^="ButtonWrapper"] {
                    display: none;
                }
            `,
            modalContent: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 0px 30px 34px 30px;
                            `,

                            css`
                                padding: 0px 80px 50px 80px;
                            `,
                        ],
                        "min",
                    );
                }}
            `,
            modalContainer: css`
                width: 100%;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-left: 8px;
                                margin-right: 8px;
                                height: auto;
                            `,
                            null,
                            css`
                                margin-left: 8px;
                                margin-right: 8px;
                            `,
                        ],
                        "min",
                    );
                }}
            `,
            scrim: css`
                display: flex;
                align-items: center;
            `,
        },
    },
    container: {
        css: css`
            displa: flex;
            flex-direction: column;
            gap: 20px;
        `,
    },
    ctaButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    logoutButton: {
        variant: "primary",
        css: css`
            width: auto;
        `,
    },
    contentWrapper: {
        css: css`
            padding-top: 0;
            display: flex;
            justify-content: center;
        `,
    },
    warning: {
        variant: "p",
        weight: 450,
        css: css`
            .billto_highlight {
                font-weight: 450;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                            line-height: 20px;
                        `,
                        null,
                        css`
                            font-size: 20px;
                            line-height: 40px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = shareListModalStyles;

const ChangeOsnTypeModal = ({ session, signOut, fields, signInPageUrl }: Props) => {
    const [isOpen, setIsOpen] = useState(false);

    const [count, setCount] = useState(+fields.timerDuration);

    useEffect(() => {
        if (count === 0) {
            signOut({
                redirectUrl: signInPageUrl
            });
        }
    }, [count]);

    useEffect(() => {
        let timer: number;
        if (getCookie("ForceRelogin") === "true") {
            setIsOpen(true);
            timer = setInterval(() => {
                setCount(prevCount => prevCount - 1);
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [session]);

    return (
        <Modal {...styles.modal} isOpen={isOpen}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.contentWrapper}>
                    <Typography {...styles.warning}>
                        {count <= 0
                            ? translate("Logging out...")
                            : siteMessage("SSO_Force_Relogin_Message")
                                  .toString()
                                  .replace("{{timer}}", count.toString())}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.ctaButtonsWrapper}>
                    <Button
                        {...styles.logoutButton}
                        onClick={() => {
                            signOut({
                                redirectUrl: signInPageUrl
                            });
                        }}
                    >
                        {fields.buttonLabel}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ChangeOsnTypeModal),
    definition: {
        group: "Common",
        displayName: "Force Relogin Modal",
        fieldDefinitions: [
            {
                name: fields.timerDuration,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                isRequired: true,
                sortOrder: 0,
            },
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                defaultValue: "Logout",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
